import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Icon from 'components/AppIcon';

const ToastContext = createContext(null);

const ICONS = {
  success: { name: 'CheckCircle', color: '#059669' },
  error:   { name: 'XCircle',     color: '#DC2626' },
  warning: { name: 'AlertTriangle', color: '#D97706' },
  info:    { name: 'Info',         color: '#D4A520' },
};

function ToastItem({ toast, onDismiss }) {
  const [dismissing, setDismissing] = useState(false);
  const icon = ICONS?.[toast?.type] || ICONS?.info;

  const dismiss = useCallback(() => {
    setDismissing(true);
    setTimeout(() => onDismiss(toast?.id), 320);
  }, [toast?.id, onDismiss]);

  useEffect(() => {
    const timer = setTimeout(dismiss, toast?.duration || 4000);
    return () => clearTimeout(timer);
  }, [dismiss, toast?.duration]);

  return (
    <div
      className={`toast toast-${toast?.type || 'info'} ${dismissing ? 'dismissing' : ''}`}
      role="alert"
      aria-live="polite"
    >
      <span className="toast-icon">
        <Icon name={icon?.name} size={18} color={icon?.color} />
      </span>
      <div className="flex-1">
        {toast?.title && (
          <p className="font-semibold text-white text-sm leading-tight mb-0.5">{toast?.title}</p>
        )}
        {toast?.message && (
          <p className="text-white/80 text-xs leading-snug">{toast?.message}</p>
        )}
      </div>
      <button
        onClick={dismiss}
        className="ml-2 text-white/50 hover:text-white transition-colors flex-shrink-0"
        aria-label="Dismiss notification"
      >
        <Icon name="X" size={14} color="currentColor" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((opts) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type: 'info', ...opts }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev?.filter(t => t?.id !== id));
  }, []);

  const toast = {
    success: (title, message, opts) => addToast({ type: 'success', title, message, ...opts }),
    error:   (title, message, opts) => addToast({ type: 'error',   title, message, ...opts }),
    warning: (title, message, opts) => addToast({ type: 'warning', title, message, ...opts }),
    info:    (title, message, opts) => addToast({ type: 'info',    title, message, ...opts }),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="toast-container" aria-label="Notifications">
        {toasts?.map(t => (
          <ToastItem key={t?.id} toast={t} onDismiss={removeToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export default ToastProvider;
